/**
 * Session health (JOSH-89).
 *
 * `reconnecting` latches the moment the bound client's `getRefreshCooldown()`
 * reports a cooldown, and stays latched after that cooldown expires: auth-js
 * nulls an expired cooldown, but no refresh has succeeded yet, so the session
 * is still reconnecting. The original `since` survives back-to-back cooldowns.
 * Only `release()` (a real refresh, sign-in, sign-out or unbind) can bring
 * health back to `ok`, and only when no cooldown is active.
 *
 * Snapshots are cached so they stay referentially stable for
 * `useSyncExternalStore`.
 */

export type SessionHealth =
  | { status: 'ok' }
  | {
      status: 'reconnecting';
      /** Device time (ms) when this reconnecting stretch began. */
      since: number;
    };

export interface HealthStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): SessionHealth;
}

export interface RefreshCooldown {
  endsAt: number;
  consecutive: number;
}

/** After this long reconnecting, the pill grows into the explanatory card. */
export const PROLONGED_RECONNECTING_MS = 2 * 60 * 1000;

const OK: SessionHealth = Object.freeze({ status: 'ok' });

/** A health store plus the hooks the guard calls. */
export function createHealthStore(readCooldown: () => RefreshCooldown | null): {
  store: HealthStore;
  /** Latches `reconnecting` if a cooldown is active; never moves to `ok`. */
  observe: () => void;
  /** Moves to `ok` unless a cooldown is still active (then it latches). */
  release: () => void;
} {
  let snapshot: SessionHealth = OK;
  const listeners = new Set<() => void>();

  const cooldownActive = (): boolean => {
    try {
      return readCooldown() !== null;
    } catch {
      return false;
    }
  };

  const set = (next: SessionHealth): void => {
    if (next === snapshot) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const latch = (): void => {
    if (snapshot.status !== 'reconnecting') set({ status: 'reconnecting', since: Date.now() });
  };

  const observe = (): void => {
    if (cooldownActive()) latch();
  };

  const release = (): void => {
    if (cooldownActive()) latch();
    else set(OK);
  };

  const store: HealthStore = {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
  };

  return { store, observe, release };
}

/**
 * Milliseconds until a reconnecting stretch counts as prolonged: 0 once it
 * has, null while health is `ok`. Both `since` and `now` are device time.
 */
export function prolongedRemainingMs(
  health: SessionHealth,
  now: number,
  thresholdMs: number = PROLONGED_RECONNECTING_MS,
): number | null {
  if (health.status !== 'reconnecting') return null;
  return Math.max(0, health.since + thresholdMs - now);
}

export interface HealthRelayOptions {
  /**
   * Calls `onRecovered` callbacks on a later macrotask, so they never run
   * inside the auth lock. Default false: they run inline.
   */
  deferRecovered?: boolean;
  /**
   * When set, a listener that throws is reported here and the others still
   * run. Default: the error propagates.
   */
  onListenerError?: (error: unknown) => void;
}

/**
 * An app-side health module: a store that reads whatever guard store is
 * connected and is `ok` while none is. Apps keep it in its own module so
 * tests that mock the client module still get a defined health.
 */
export interface HealthRelay {
  /** Connects a guard's health store; null disconnects it. */
  connect(store: HealthStore | null): void;
  /** Store shaped for `useSyncExternalStore`; snapshots are referentially stable. */
  store: HealthStore;
  /**
   * Calls `callback` once per move from `reconnecting` to `ok`. Returns the
   * unsubscribe function.
   */
  onRecovered(callback: () => void): () => void;
}

/** Creates a health relay; see `HealthRelayOptions` for the two behaviors. */
export function createHealthRelay({ deferRecovered = false, onListenerError }: HealthRelayOptions = {}): HealthRelay {
  let source: HealthStore | null = null;
  let detachSource: (() => void) | null = null;
  const listeners = new Set<() => void>();

  const notify = (): void => {
    listeners.forEach((listener) => {
      if (!onListenerError) {
        listener();
        return;
      }
      try {
        listener();
      } catch (error) {
        onListenerError(error);
      }
    });
  };

  const store: HealthStore = {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => source?.getSnapshot() ?? OK,
  };

  return {
    connect(next) {
      detachSource?.();
      detachSource = null;
      source = next;
      if (next) detachSource = next.subscribe(notify);
      notify();
    },
    store,
    onRecovered(callback) {
      let previous = store.getSnapshot().status;
      return store.subscribe(() => {
        const current = store.getSnapshot().status;
        const recovered = previous === 'reconnecting' && current === 'ok';
        previous = current;
        if (!recovered) return;
        if (deferRecovered) setTimeout(callback, 0);
        else callback();
      });
    },
  };
}
